// ajay:code 19-08-26
import { Trash2 } from "lucide-react";
import MultiLineList from "./MultiLineList";

const numInput = {
  width: 56,
  padding: "4px 6px",
  fontSize: 12,
  borderRadius: 6,
  border: "1px solid var(--border2)",
  background: "var(--surface2)",
  color: "var(--text)",
  textAlign: "center",
};

// severity is NOT stored here — it lives on the parent Failure Mode and is
// passed in only for the live RPN preview. The authoritative RPN is always
// recomputed server-side on save.
function liveRpn(severity, occurrence, detection) {
  if (severity == null || occurrence === "" || detection === "") return "—";
  const s = Number(severity),
    o = Number(occurrence),
    d = Number(detection);
  if (Number.isNaN(s) || Number.isNaN(o) || Number.isNaN(d)) return "—";
  return s * o * d;
}

export default function CauseRow({
  cause,
  severity,
  onChange,
  onDelete,
  readOnly,
}) {
  const set = (field, value) => onChange({ ...cause, [field]: value });

  const rpn = liveRpn(severity, cause.occurrence, cause.detection);
  const revisedSeverity = cause.revised_severity ?? severity;
  const revisedRpn = liveRpn(
    revisedSeverity,
    cause.revised_occurrence,
    cause.revised_detection,
  );

  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 8,
        padding: 10,
        marginBottom: 8,
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 10,
              color: "var(--text3)",
              fontWeight: 700,
              marginBottom: 3,
            }}
          >
            CAUSE
          </div>
          {readOnly ? (
            <div style={{ fontSize: 12 }}>{cause.cause_text || "—"}</div>
          ) : (
            <input
              type="text"
              value={cause.cause_text || ""}
              placeholder="e.g. Less injection pressure"
              onChange={(e) => set("cause_text", e.target.value)}
              style={{
                width: "100%",
                padding: "5px 8px",
                fontSize: 12,
                borderRadius: 6,
                border: "1px solid var(--border2)",
                background: "var(--surface2)",
                color: "var(--text)",
              }}
            />
          )}
        </div>
        {!readOnly && (
          <button
            type="button"
            title="Delete cause"
            onClick={onDelete}
            style={{
              background: "none",
              border: "none",
              color: "var(--danger)",
              cursor: "pointer",
              marginTop: 16,
            }}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {/* Occurrence / Detection / RPN row */}
      <div
        style={{
          display: "flex",
          gap: 14,
          alignItems: "flex-end",
          marginTop: 8,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ fontSize: 10, color: "var(--text3)", fontWeight: 700 }}>
            OCCURRENCE
          </div>
          <div style={{ fontSize: 9, color: "var(--text3)", marginBottom: 2 }}>
            1 (rare) – 10 (frequent)
          </div>
          {readOnly ? (
            <div style={{ fontSize: 13, fontWeight: 700 }}>
              {cause.occurrence ?? "—"}
            </div>
          ) : (
            <input
              type="number"
              min={1}
              max={10}
              style={numInput}
              value={cause.occurrence ?? ""}
              onChange={(e) =>
                set("occurrence", e.target.value === "" ? "" : +e.target.value)
              }
            />
          )}
        </div>
        <div>
          <div style={{ fontSize: 10, color: "var(--text3)", fontWeight: 700 }}>
            DETECTION
          </div>
          <div style={{ fontSize: 9, color: "var(--text3)", marginBottom: 2 }}>
            1 (certain) – 10 (unlikely)
          </div>
          {readOnly ? (
            <div style={{ fontSize: 13, fontWeight: 700 }}>
              {cause.detection ?? "—"}
            </div>
          ) : (
            <input
              type="number"
              min={1}
              max={10}
              style={numInput}
              value={cause.detection ?? ""}
              onChange={(e) =>
                set("detection", e.target.value === "" ? "" : +e.target.value)
              }
            />
          )}
        </div>
        <div>
          <div style={{ fontSize: 10, color: "var(--text3)", fontWeight: 700 }}>
            R.P.N.
          </div>
          <div
            title="Severity × Occurrence × Detection — auto-calculated, not editable"
            style={{
              fontSize: 14,
              fontWeight: 800,
              padding: "4px 12px",
              borderRadius: 6,
              background: "rgba(0,194,255,0.1)",
              color: "var(--accent)",
              border: "1px solid var(--accent)",
              minWidth: 40,
              textAlign: "center",
            }}
          >
            {rpn}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
          marginTop: 10,
        }}
      >
        <MultiLineList
          label="Prevention Control"
          lines={cause.control_prevention || []}
          onChange={(lines) => set("control_prevention", lines)}
          readOnly={readOnly}
          placeholder="e.g. Process Monitoring Record..."
        />
        <MultiLineList
          label="Detection Control"
          lines={cause.control_detection || []}
          onChange={(lines) => set("control_detection", lines)}
          readOnly={readOnly}
          placeholder="e.g. PLC Controller"
        />
      </div>

      {/* Recommended Action / Responsibility / Target Date */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr 1fr",
          gap: 10,
          marginTop: 8,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10,
              color: "var(--text3)",
              fontWeight: 700,
              marginBottom: 3,
            }}
          >
            RECOMMENDED ACTION
          </div>
          {readOnly ? (
            <div style={{ fontSize: 12 }}>
              {cause.recommended_action || "—"}
            </div>
          ) : (
            <textarea
              rows={2}
              value={cause.recommended_action || ""}
              onChange={(e) => set("recommended_action", e.target.value)}
              style={{
                width: "100%",
                padding: "5px 8px",
                fontSize: 12,
                borderRadius: 6,
                border: "1px solid var(--border2)",
                background: "var(--surface2)",
                color: "var(--text)",
              }}
            />
          )}
        </div>
        <div>
          <div
            style={{
              fontSize: 10,
              color: "var(--text3)",
              fontWeight: 700,
              marginBottom: 3,
            }}
          >
            RESPONSIBILITY
          </div>
          {readOnly ? (
            <div style={{ fontSize: 12 }}>{cause.responsibility || "—"}</div>
          ) : (
            <input
              type="text"
              value={cause.responsibility || ""}
              onChange={(e) => set("responsibility", e.target.value)}
              style={{
                width: "100%",
                padding: "5px 8px",
                fontSize: 12,
                borderRadius: 6,
                border: "1px solid var(--border2)",
                background: "var(--surface2)",
                color: "var(--text)",
              }}
            />
          )}
        </div>
        <div>
          <div
            style={{
              fontSize: 10,
              color: "var(--text3)",
              fontWeight: 700,
              marginBottom: 3,
            }}
          >
            TARGET DATE
          </div>
          {readOnly ? (
            <div style={{ fontSize: 12 }}>{cause.target_date || "—"}</div>
          ) : (
            <input
              type="date"
              value={cause.target_date || ""}
              onChange={(e) => set("target_date", e.target.value)}
              style={{
                width: "100%",
                padding: "4px 6px",
                fontSize: 12,
                borderRadius: 6,
                border: "1px solid var(--border2)",
                background: "var(--surface2)",
                color: "var(--text)",
              }}
            />
          )}
        </div>
      </div>

      {/* Action Results — Action Taken + revised Sev/Occ/Det/RPN */}
      <div
        style={{
          marginTop: 10,
          paddingTop: 8,
          borderTop: "1px dashed var(--border2)",
        }}
      >
        <div
          style={{
            fontSize: 10,
            color: "var(--accent2)",
            fontWeight: 700,
            marginBottom: 6,
          }}
        >
          ACTION RESULTS (after corrective action)
        </div>
        <MultiLineList
          label="Action Taken"
          lines={cause.action_taken || []}
          onChange={(lines) => set("action_taken", lines)}
          readOnly={readOnly}
        />
        <div
          style={{
            display: "flex",
            gap: 14,
            alignItems: "flex-end",
            flexWrap: "wrap",
            marginTop: 4,
          }}
        >
          <div>
            <div
              style={{ fontSize: 10, color: "var(--text3)", fontWeight: 700 }}
            >
              REVISED SEV
            </div>
            {readOnly ? (
              <div style={{ fontSize: 12 }}>
                {cause.revised_severity ?? "—"}
              </div>
            ) : (
              <input
                type="number"
                min={1}
                max={10}
                style={numInput}
                value={cause.revised_severity ?? ""}
                onChange={(e) =>
                  set(
                    "revised_severity",
                    e.target.value === "" ? "" : +e.target.value,
                  )
                }
              />
            )}
          </div>
          <div>
            <div
              style={{ fontSize: 10, color: "var(--text3)", fontWeight: 700 }}
            >
              REVISED OCC
            </div>
            {readOnly ? (
              <div style={{ fontSize: 12 }}>
                {cause.revised_occurrence ?? "—"}
              </div>
            ) : (
              <input
                type="number"
                min={1}
                max={10}
                style={numInput}
                value={cause.revised_occurrence ?? ""}
                onChange={(e) =>
                  set(
                    "revised_occurrence",
                    e.target.value === "" ? "" : +e.target.value,
                  )
                }
              />
            )}
          </div>
          <div>
            <div
              style={{ fontSize: 10, color: "var(--text3)", fontWeight: 700 }}
            >
              REVISED DET
            </div>
            {readOnly ? (
              <div style={{ fontSize: 12 }}>
                {cause.revised_detection ?? "—"}
              </div>
            ) : (
              <input
                type="number"
                min={1}
                max={10}
                style={numInput}
                value={cause.revised_detection ?? ""}
                onChange={(e) =>
                  set(
                    "revised_detection",
                    e.target.value === "" ? "" : +e.target.value,
                  )
                }
              />
            )}
          </div>
          <div>
            <div
              style={{ fontSize: 10, color: "var(--text3)", fontWeight: 700 }}
            >
              REVISED R.P.N.
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 800,
                padding: "4px 10px",
                borderRadius: 6,
                background: "rgba(0,224,150,0.1)",
                color: "var(--success)",
                border: "1px solid var(--success)",
                minWidth: 36,
                textAlign: "center",
              }}
            >
              {revisedRpn}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
